/**
 * The two informational pages, and nothing that grows.
 *
 * `[x]` §20.17 names About Us and Contact Us in **one** row, so they are one
 * management surface with two entries rather than two features — which is why
 * the navigation column carries a single entry pointing at `/pages/about` and
 * the two-entry strip is what makes the second route reachable at all.
 *
 * **There is no `/pages` landing route.** The pack's sitemap carries
 * `/pages/about` and `/pages/contact` and nothing between them, so a crumb
 * naming an intermediate level would point at a route that does not exist.
 */

export const PAGE_SLUGS = ['about', 'contact'] as const;

export type PageSlug = (typeof PAGE_SLUGS)[number];

export const PAGES_ROUTES: Readonly<Record<PageSlug, string>> = {
  about: '/pages/about',
  contact: '/pages/contact',
};

/**
 * The public address each page is edited for.
 *
 * **A deployment fact, not a product setting.** The public site is
 * `shahedni.com` per the platform architecture's §1.2 host table; it is not a row
 * of the catalog's «Configuration, not features» table and there is no endpoint
 * that serves it. It is rendered read-only, so an operator knows which of the
 * two surfaces they are writing into without reading the breadcrumb back.
 */
const PUBLIC_HOST = 'shahedni.com';

export const PUBLIC_PATH: Readonly<Record<PageSlug, string>> = {
  about: `${PUBLIC_HOST}/about`,
  contact: `${PUBLIC_HOST}/contact`,
};

export function isPageSlug(value: string | undefined): value is PageSlug {
  return value === 'about' || value === 'contact';
}
